import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import {
  servicesMenu,
  servicesPrimaryCards,
  categoryIcons,
} from "@/data/site/navigation";
import { brand } from "@/data/site/brand";

interface Props {
  onClose: () => void;
}

export const SiteServicesDropdown = ({ onClose }: Props) => {
  return (
    <div className="border-b border-border shadow-xl" style={{ backgroundColor: "#ffffff" }}>
      <div className="container mx-auto px-6 py-8">
        {/* Section label */}
        <div className="flex items-baseline justify-between mb-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-navy/60">
            What's your tax problem?
          </span>
          <Link
            to="/services"
            onClick={onClose}
            className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-navy hover:text-navy-dark transition-colors"
          >
            See all services <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Primary problem cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {servicesPrimaryCards.map((card) => {
            const Icon = card.Icon;
            return (
              <Link
                key={card.slug}
                to={card.href}
                onClick={onClose}
                className="group rounded-lg border border-border bg-white hover:border-gold hover:shadow-md hover:-translate-y-0.5 transition-all p-4 flex flex-col"
              >
                <span className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mb-3 shrink-0 text-navy group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                  <Icon className="w-5 h-5" />
                </span>
                <h4 className="font-heading font-bold text-sm leading-snug text-navy mb-1.5 text-balance">
                  {card.headline}
                </h4>
                <p className="text-xs text-muted-foreground leading-snug">
                  {card.desc}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Footer: browse-by-category pills + CTAs */}
        <div className="mt-6 pt-5 border-t border-border grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 lg:gap-8 items-center">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-navy/50 mr-2">
              Browse by category
            </span>
            {servicesMenu.map((col, idx) => {
              const href = col.href ?? "/services";
              const Icon = categoryIcons[href];
              return (
                <span key={col.title} className="flex items-center gap-1.5">
                  {idx > 0 && <span className="text-border">·</span>}
                  {Icon && <Icon className="w-4 h-4 text-navy/70" aria-hidden="true" />}
                  <Link
                    to={href}
                    onClick={onClose}
                    className="text-sm font-semibold text-navy hover:text-navy-dark transition-colors"
                  >
                    {col.title}
                  </Link>
                </span>
              );
            })}
          </div>

          <div className="flex items-center gap-3 justify-start lg:justify-end">
            <Link
              to="/services"
              onClick={onClose}
              className="sm:hidden inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy hover:text-navy-dark transition-colors"
            >
              See all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a
              href={`tel:${brand.phoneTel}`}
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md bg-navy-dark hover:bg-navy text-white px-4 py-2 text-sm font-semibold transition-colors"
            >
              <Phone className="w-4 h-4 text-gold" /> {brand.phone}
            </a>
            <Link
              to="/free-consultation"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-navy-dark hover:text-navy transition-colors"
            >
              Free case review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
